import React, {useState, useEffect, useRef} from 'react';
import {useForm} from 'react-hook-form';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTitle} from '../../../components/General/Layout';
import LoadingWheel from '../../../components/General/LoadingWheel';
import MaintenanceHeader from '../../../components/Surveys/SurveyMaintenanceHeader';
import ErrorHelper from '../../../service/ErrorHelper';
import {SMaster, Survey} from '../../../service/Urls';
import {get, post} from '../../../service/ApiService';
import SatisfactionSection from '../../../components/Surveys/SatisfactionSection';
import {Box, Flex, Spacer, Text, Textarea, Spinner} from '@chakra-ui/react';
import SurveySideBar from '../../../components/Surveys/SurveySideBar';
import FlexCard from '../../../components/General/FlexCard';
import SatisfactionToTen from '../../../components/Surveys/SatisfactionToTen';
import {CustomCheckbox} from '../../../components/General/FormInputs';
import BoxCard from '../../../components/General/BoxCard';
import {formatDate} from '../../../helpers/Functions';
import PreArrangeRadio from '../../../components/Surveys/PreArrangeRadio';

const FuneralMaintenance = () => {
  const [data, setData] = useState({});
  const [smasterData, setSmasterData] = useState({});
  const [toast, setToast] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdate, setIsUpdate] = useState(true);
  const [ids, setIds] = useState([]);
  const [title, setTitle] = useTitle('Funeral Survey Maintenance');
  const navigate = useNavigate();
  const location = useLocation();
  const isMounted = useRef(false);


  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: {errors},
    getValues,
  } = useForm();

  useEffect(() => {
    isMounted.current = true;

    loadData(location?.state?.id);

    return () => {
      isMounted.current = false;
    };
  }, []);

  const loadData = async masterId => {
    const data = await get(Survey.GET + `/` + masterId);
    setToast(data);
    if (!data.error) {
      if (!data.dateAdded) {
        data.dateAdded = localStorage.getItem('prevDateAdded') || new Date().toISOString().split('T')[0];
        setIsUpdate(false);
      } else {
        // For a datepicker to default a value it must be in yyyy-mm-dd.  Modify initial value then call reset on the data to get that defaulted
        data.dateAdded = data.dateAdded.split('T')[0];
      }
      setData(data);
      reset(data);
      const smasterResponse = await get(SMaster.RECORD + '/' + masterId);
      setToast(smasterResponse);
      if (!smasterResponse.error) {
        smasterResponse['contractDate'] = formatDate(smasterResponse.smCondate);
        setSmasterData(smasterResponse);
      }
    }
    setIsLoading(false);
    setTitle('Funeral Survey Maintenance');
    assignIds();
  };

  

  const assignIds = () => {
    let arr = [];
    const prompts = [prompt2, prompt3, prompt4, prompt5, prompt6];
    prompts.forEach(promptArr => {
      arr = arr.concat(promptArr.map(prompt => prompt.id));
    });
    setIds(arr);
  };

  // Function to check if Negative Survey should be automatically selected
  const shouldAutoSelectNegativeSurvey = (formValues) => {
    if (!formValues) return false;
    
    // Check if any radio button in "Poor" category is selected (value = 4)
    // Exclude sv88a (Recommendation field) as it has its own separate condition
    const poorSelected = Object.entries(formValues).some(([key, value]) => value === 4 && key !== 'sv88a');
    console.log('Poor selected:', poorSelected); // Debug log
    console.log('All form values:', Object.entries(formValues).filter(([key, value]) => value === 4 && key !== 'sv88a')); // Debug log - show which fields have value 4 (excluding sv88a)
    if (poorSelected) return true;
    
    // Check if three or more radio buttons in "Fair" category are selected (value = 3)
    // Exclude recommendation field sv88a from the fair tally
    const fairFields = Object.entries(formValues).filter(([key, value]) => value === 3 && key !== 'sv88a');
    const fairCount = fairFields.length;
    console.log('Fair count:', fairCount); // Debug log
    console.log('Fair fields:', fairFields); // Debug log - show which fields have Fair value
    if (fairCount >= 3) return true;
    
    // Check if any radio button from "8 a. Recommendation" is selected (values 6,5,4,3,2,1,0,N/A)
    // These correspond to SatisfactionOneToTen values: 3,4,5,6,7,8,9,10,11,12 (excluding 1,2 which are 9,10)
    const recommendationValue = formValues.sv88a;
    console.log('Recommendation value:', recommendationValue); // Debug log
    console.log('Recommendation condition (>=5 && <=12):', recommendationValue && recommendationValue >= 5 && recommendationValue <= 12); // Debug log
    console.log('Recommendation mapping - if value is 3, that means rating 8 was selected'); // Debug log
    if (recommendationValue && recommendationValue >= 5 && recommendationValue <= 12) {
      console.log('Recommendation triggered auto-selection'); // Debug log
      return true;
    }
    
    return false;
  };

  // Function to check if Negative Survey should be automatically unchecked
  const shouldAutoUncheckNegativeSurvey = (formValues) => {
    if (!formValues) return false;

    // 1) No Poor selected (exclude recommendation field)
    const poorSelected = Object.entries(formValues).some(([key, value]) => value === 4 && key !== 'sv88a');

    // 2) Fair count below threshold (exclude recommendation field)
    const fairCount = Object.entries(formValues).filter(([key, value]) => value === 3 && key !== 'sv88a').length;
    console.log('Fair count for uncheck:', fairCount); // Debug log

    // 3) Recommendation is NOT in negative set (i.e., 10 or 9 or unset)
    const recommendationValue = formValues.sv88a;
    console.log('Recommendation value for uncheck:', recommendationValue); // Debug log
    const recommendationIsNegative = !!recommendationValue && recommendationValue >= 5 && recommendationValue <= 12;

    // Uncheck when none of the negative conditions are met
    return !poorSelected && fairCount < 3 && !recommendationIsNegative;
  };

  // Auto-select or unselect Negative Survey checkbox based on conditions
  const checkAndAutoSelectNegativeSurvey = () => {
    const formValues = getValues();
    console.log('Form values:', formValues); // Debug log
    console.log('Should auto-select negative:', shouldAutoSelectNegativeSurvey(formValues)); // Debug log
    console.log('Should auto-uncheck negative:', shouldAutoUncheckNegativeSurvey(formValues)); // Debug log
    
    if (shouldAutoSelectNegativeSurvey(formValues)) {
      console.log('Auto-selecting Negative Survey checkbox'); // Debug log
      setData(prevData => ({
        ...prevData,
        svNeg: true,
        svPos: false,
        svNeedatt: false
      }));
    } else if (shouldAutoUncheckNegativeSurvey(formValues)) {
      console.log('Auto-unchecking Negative Survey checkbox'); // Debug log
      setData(prevData => ({
        ...prevData,
        svNeg: false,
        svPos: false,
        svNeedatt: false
      }));
    }
  };

  const submitSurvey = async formData => {
    formData.svRecord = data.svRecord ?? smasterData.smRecord;
    formData.svClient = data.svClient ?? smasterData.smClient;
    formData.svLoc = data.svLoc ?? smasterData.smLoc;
    formData.svGroup = data.svGroup ?? smasterData.smGroup;

    formData.clientId = smasterData.nclientpk;
    formData.groupId = smasterData.ngrouppk;
    formData.locationId = smasterData.locationpk;
    formData.sMasterId = smasterData.npk;

    formData.svPos = data.svPos;
    formData.svNeg = data.svNeg;
    formData.svNeedatt = data.svNeedatt;

    let url = Survey.ADD_FUNERAL;
    let successMessage = 'Successfully added survey for ' + smasterData.clientName;
    if (isUpdate) {
      url = Survey.UPDATE_FUNERAL + '/' + formData.id;
      successMessage = 'Successfully updated survey for ' + smasterData.clientName;
    }

    setIsSaving(true);

    const response = await post(url, formData);

    if (isMounted.current === false) return;

    setIsSaving(false);

    setToast({
      response: response,
      successMessage: successMessage,
      onSuccess: () => navigate('/surveys/lookup', {state: {serviceType: location?.state?.serviceType}}),
    });
  };

  // The ids must correspond to the data that is sent from api, otherwise defaults and sending data up won't work
  const prompt2 = [
    {prompt: '2a. Response', id: 'sv2a'},
    {prompt: 'b. Greet', id: 'sv2b'},
    {prompt: 'c. Care', id: 'sv2c'},
  ];
  const prompt3 = [
    {prompt: '3a. Needs', id: 'sv3a'},
    {prompt: 'b. Wishes', id: 'sv3b'},
    {prompt: 'c. Explain', id: 'sv3c'},
    {prompt: 'd. Detail', id: 'sv3d'},
    {prompt: 'e. Knowledge', id: 'sv3e'},
    {prompt: 'f. Sincere', id: 'sv3f'},
    {prompt: 'g. Answers', id: 'sv3g'},
    {prompt: 'h. Payment', id: 'sv3h'},
  ];

  const prompt4 = [
    {prompt: '4a. Facilities', id: 'sv4a'},
    {prompt: 'b. Vehicles', id: 'sv4b'},
  ];

  const prompt5 = [
    {prompt: '5a. Service', id: 'sv5a'},
    {prompt: 'b. Appearance', id: 'sv5b'},
    {prompt: 'c. Professional', id: 'sv5c'},
    {prompt: 'd. Transfer', id: 'sv5d'},
    {prompt: 'e. Presentation', id: 'sv5e'},
    {prompt: 'f. Visitation', id: 'sv5f'},
    {prompt: 'g. Ceremony', id: 'sv5g'},
    {prompt: 'h. Support', id: 'sv5h'},
  ];

  const prompt6 = [
    {prompt: '6a. Value', id: 'sv6a'},
    {prompt: 'b. Satisfaction', id: 'sv7a'},
  ];

  return (
    <>
      {isLoading ? (
        <>
          <LoadingWheel />
          <ErrorHelper toastResponse={toast} />
        </>
      ) : (
        <form onSubmit={handleSubmit(submitSurvey)}>
          <Box w={{base: '100%', lg: '90%'}}>
            <MaintenanceHeader
              includeCovid
              register={register}
              control={control}
              errors={errors}
              masterData={smasterData}
              data={data}
              setData={setData}
              reset={reset}
              ids={ids}
              getValues={getValues}
              submit={handleSubmit(submitSurvey)}
            />

            

            <Flex>
              <Box w="40%">
                <BoxCard textAlign="start">
                  <Flex>
                    <Text mr="4">1.</Text>
                    <Box textAlign="start">
                      <CustomCheckbox name="Pre-Need" id={'sv1a'} control={control} defaultChecked={data.sv1a} />
                      <CustomCheckbox name="Advertising" id={'sv1f'} control={control} defaultChecked={data.sv1f} />
                      <CustomCheckbox name="Location" id={'sv1c'} control={control} defaultChecked={data.sv1c} />
                      <CustomCheckbox name="Recommendation" id={'sv1b'} control={control} defaultChecked={data.sv1b} />
                    </Box>
                    <Spacer />
                    <Box textAlign="start">
                      <CustomCheckbox name="Reputation" id={'sv1d'} control={control} defaultChecked={data.sv1d} />
                      <CustomCheckbox name="Previous Experience" id={'sv1e'} control={control} defaultChecked={data.sv1e} />
                      <CustomCheckbox name="Deceased Wishes" id={'sv1g'} control={control} defaultChecked={data.sv1g} />
                      <CustomCheckbox name="Website" id={'sv1j'} control={control} defaultChecked={data.sv1j} />
                    </Box>
                  </Flex>
                  <Textarea rows={1} id="sv1fd" name="Reccomendation" {...register('sv1fd')} errors={errors} />
                  <CustomCheckbox name="Other" id={'sv1h'} control={control} defaultChecked={data.sv1h} ml="7" />
                  <Textarea rows={1} id="sv1hd" name="Other" {...register('sv1hd')} errors={errors} />
                </BoxCard>
                <SatisfactionSection control={control} prompts={prompt2} data={data} onRadioChange={checkAndAutoSelectNegativeSurvey} />
                <SatisfactionSection control={control} prompts={prompt3} data={data} onRadioChange={checkAndAutoSelectNegativeSurvey} />
              </Box>
              <Box w="40%">
                <SatisfactionSection control={control} prompts={prompt4} data={data} onRadioChange={checkAndAutoSelectNegativeSurvey} />
                <SatisfactionSection control={control} prompts={prompt5} data={data} onRadioChange={checkAndAutoSelectNegativeSurvey} />
                <SatisfactionSection control={control} prompts={prompt6} data={data} onRadioChange={checkAndAutoSelectNegativeSurvey} />
                <SatisfactionToTen control={control} question={{prompt: '8 a. Recommendation', id: 'sv88a'}} data={data} onRadioChange={checkAndAutoSelectNegativeSurvey} />
                <FlexCard>
                  <Text>9a. Pre-Arrange</Text>
                  <PreArrangeRadio control={control} id="sv9a" name="9a. Pre-Arrange" data={data} />
                </FlexCard>
              </Box>
              <Box w="20%">
                <SurveySideBar
                  control={control}
                  includes={{chinese: true, surveyFormat: true, recommendations: true}}
                  data={data}
                  register={register}
                  getValues={getValues}
                  reset={reset}
                  errors={errors}
                  svcType={location?.state?.serviceType}
                  sMasterId={location?.state?.id}
                  isSaving={isSaving}
                  setData={setData}
                />
              </Box>
            </Flex>
            <FlexCard>
              <Textarea rows={3} id="svComment" name="svComment" {...register('svComment')} errors={errors} />
            </FlexCard>
          </Box>
          <ErrorHelper toastResponse={toast} />
        </form>
      )}

      
    </>
  );
};

export default FuneralMaintenance;
